import { useState } from 'react';
import { Head, Link } from '@inertiajs/react';
import TeacherShell from '../../../components/layout/TeacherShell';
import { Card } from '../../../components/ui';
import { Check, X, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/cn';
import FlagQuestionButton from '../../../components/vocabulary/shared/FlagQuestionButton';

/**
 * Async teacher session replay page.
 *
 * Renders one row per stage / item the student walked through, sourced
 * from Vocabulary::SessionReplayBuilder's payload. Each row is collapsed
 * by default; expanding shows the full prompt + response + verdict + AI
 * feedback. MVP — static reconstruction, no scrubbable playback.
 *
 * Props (Inertia):
 *   student — { id, full_name, first_name, email }
 *   replay  — { session: {...meta}, items: [...] } from SessionReplayBuilder
 */
export default function TeacherVocabularySessionReplaysShow({ student, replay }) {
  const { session, items } = replay;
  const flagEndpoint = `/teacher/users/${student.id}/vocabulary_flags`;

  return (
    <TeacherShell
      scrollTitle={
        <span>
          <Link
            href={`/teacher/users/${student.id}`}
            className="hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            {student.full_name}
          </Link>{' '}
          · Session #{session.id}
        </span>
      }
    >
      <Head title={`${student.first_name} — Session #${session.id}`} />

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <SessionMetaCard student={student} session={session} />

        <div className="space-y-2">
          <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Walk-through ({items.length} {items.length === 1 ? 'item' : 'items'})
          </h2>
          {items.length === 0 ? (
            <Card className="p-6 text-sm text-gray-500 dark:text-gray-400 text-center italic">
              No items recorded for this session.
            </Card>
          ) : (
            <div className="space-y-1.5">
              {items.map((item, idx) => (
                <ReplayItem
                  key={`${item.stage_or_format}-${item.card_id ?? 'batch'}-${idx}`}
                  item={item}
                  flagEndpoint={flagEndpoint}
                  sessionId={session.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </TeacherShell>
  );
}

function SessionMetaCard({ student, session }) {
  const typeLabel = session.type === 'review' ? 'Practice (review)' : 'Onboarding';
  const completedAt = session.completed_at ? new Date(session.completed_at) : null;
  const startedAt = session.started_at ? new Date(session.started_at) : null;
  const duration =
    completedAt && startedAt
      ? formatDurationMinutes((completedAt - startedAt) / 1000)
      : null;

  return (
    <Card className="p-5 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
            {typeLabel}
          </p>
          <p className="text-lg font-medium text-gray-900 dark:text-white">
            {student.full_name} — Session #{session.id}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {session.word_count} {session.word_count === 1 ? 'word' : 'words'}
            {duration ? ` · ${duration}` : ''}
            {' · '}
            <StatusPill status={session.status} />
          </p>
          {session.lemmas && session.lemmas.length > 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-300 pt-1">
              <span className="text-gray-400 dark:text-gray-500">Words: </span>
              <span className="font-serif italic">{session.lemmas.join(', ')}</span>
            </p>
          )}
          {session.cards_missing > 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400 pt-1">
              {session.cards_missing} {session.cards_missing === 1 ? 'card was' : 'cards were'} removed since this session — full prompt detail not available for those items.
            </p>
          )}
        </div>
        <div className="text-right text-xs text-gray-400 dark:text-gray-500 space-y-0.5 flex-shrink-0">
          {startedAt && <div>Started {startedAt.toLocaleString()}</div>}
          {completedAt && <div>Completed {completedAt.toLocaleString()}</div>}
        </div>
      </div>
    </Card>
  );
}

function StatusPill({ status }) {
  const styles = {
    completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    in_progress: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    abandoned: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    paused: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  };
  return (
    <span className={cn('inline-block px-2 py-0.5 rounded-full text-xs font-medium', styles[status] || styles.abandoned)}>
      {status.replace('_', ' ')}
    </span>
  );
}

function ReplayItem({ item, flagEndpoint, sessionId }) {
  const [open, setOpen] = useState(false);
  const labelText = stageOrFormatLabel(item.stage_or_format);
  const outcome = outcomeFor(item);

  return (
    <Card className="overflow-hidden">
      <div className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-3 text-left flex-1 min-w-0"
        >
          <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </span>
          <span className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 w-28 flex-shrink-0">
            {labelText}
          </span>
          <span className="font-serif text-base text-gray-900 dark:text-white flex-1 truncate">
            {item.lemma || (item.card_id ? `card #${item.card_id}` : '—')}
            {item.card_missing && (
              <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">(card removed)</span>
            )}
          </span>
        </button>
        <FlagQuestionButton
          enabled={Boolean(flagEndpoint && item.card_id)}
          endpoint={flagEndpoint}
          payload={{
            user_word_card_id: item.card_id,
            vocabulary_session_id: sessionId,
            stage_or_format: item.stage_or_format,
          }}
        />
        <OutcomeBadge outcome={outcome} />
      </div>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-800 space-y-3 text-sm">
          <ItemPrompt item={item} />
          <ItemResponse item={item} />
          <ItemVerdict item={item} />
          {item.ai_feedback && (
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">AI feedback</div>
              <div className="italic text-gray-700 dark:text-gray-300">{item.ai_feedback}</div>
            </div>
          )}
          {item.timing?.time_taken_ms != null && (
            <div className="text-xs text-gray-400 dark:text-gray-500">
              took {(item.timing.time_taken_ms / 1000).toFixed(1)}s
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function ItemPrompt({ item }) {
  const p = item.prompt;
  if (!p || p.unavailable) {
    return (
      <div>
        <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Prompt</div>
        <div className="text-gray-500 dark:text-gray-400 italic">
          Prompt not reconstructable — {p?.reason || 'unknown reason'}.
        </div>
      </div>
    );
  }

  // Onboarding stage payloads carry `payload` from build_stage_payload;
  // review queue items carry the raw payload. Unify the access pattern.
  const payload = p.payload || p;
  const stage = item.stage_or_format;

  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Prompt</div>
      {stage === 'stage_1' && (
        <div className="space-y-1">
          <div className="font-serif italic text-gray-700 dark:text-gray-300">
            "{payload.context_sentence}"
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {payload.prompt || `In this context, ${payload.lemma} means ___`}
          </div>
        </div>
      )}
      {stage === 'stage_2_5' && (
        <div className="space-y-1">
          {payload.context_hint && (
            <div className="font-serif italic text-gray-700 dark:text-gray-300">
              "{payload.context_hint}"
            </div>
          )}
          <div className="text-gray-700 dark:text-gray-300">Which definition fits <b>{payload.lemma}</b>?</div>
          {payload.options && (
            <ul className="text-sm list-none space-y-0.5 pl-1">
              {payload.options.map((o) => (
                <li key={o.id}>
                  <span className="text-gray-400 dark:text-gray-500 mr-2">{o.id.toUpperCase()}</span>
                  {o.text}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {stage === 'stage_3' && (
        <div className="space-y-1">
          <div className="text-gray-700 dark:text-gray-300">Sort the bank words under the right column.</div>
          {payload.columns && (
            <div className="text-sm">
              <span className="text-gray-400 dark:text-gray-500">Columns: </span>
              <span className="font-serif italic">{payload.columns.map((c) => c.lemma).join(' · ')}</span>
            </div>
          )}
          {payload.bank && (
            <div className="text-sm">
              <span className="text-gray-400 dark:text-gray-500">Bank: </span>
              {payload.bank.map((b) => b.lemma).join(', ')}
            </div>
          )}
        </div>
      )}
      {stage === 'stage_4' && (
        <div className="space-y-1">
          <div className="font-serif text-gray-700 dark:text-gray-300">
            {payload.sentence_with_blank}
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500">
            answer: <span className="font-mono">{payload.accepted_surface_form}</span>
            {payload.first_letter && <> · hint: <span className="font-mono">{payload.first_letter}{'_'.repeat(payload.remaining_length || 0)}</span></>}
          </div>
        </div>
      )}
      {item.kind === 'review_item' && stage === 'cloze' && (
        <div className="space-y-1">
          <div className="font-serif text-gray-700 dark:text-gray-300">
            {payload.sentence_with_blank}
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500">
            answer: <span className="font-mono">{payload.accepted_surface_form}</span>
          </div>
        </div>
      )}
      {item.kind === 'review_item' && stage === 'mcq_def_to_word' && (
        <div className="space-y-1">
          <div className="text-gray-700 dark:text-gray-300 italic">"{payload.definition}"</div>
          {payload.choices && (
            <ul className="text-sm list-none space-y-0.5 pl-1">
              {payload.choices.map((c) => (
                <li key={c.id}>
                  <span className="text-gray-400 dark:text-gray-500 mr-2">{c.id.toUpperCase()}</span>
                  <span className="font-serif">{c.lemma}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {item.kind === 'review_item' && stage === 'sentence_writing' && (
        <div className="space-y-1">
          <div className="text-gray-700 dark:text-gray-300">Write a sentence using <b>{payload.lemma || item.lemma}</b>.</div>
          {payload.definition && (
            <div className="text-xs text-gray-400 dark:text-gray-500 italic">{payload.definition}</div>
          )}
        </div>
      )}
    </div>
  );
}

function ItemResponse({ item }) {
  const r = item.response;
  if (!r) return null;
  const stage = item.stage_or_format;

  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Response</div>
      {stage === 'stage_1' && r.attempts && (
        <div className="space-y-1">
          {r.attempts.map((a, i) => (
            <div key={i} className="flex items-baseline gap-2 text-sm">
              <span className="text-gray-400 dark:text-gray-500 w-12">#{i + 1}</span>
              <span className="font-mono text-gray-800 dark:text-gray-200">"{a.response}"</span>
              <TierPill tier={a.tier} />
            </div>
          ))}
        </div>
      )}
      {stage === 'stage_2_5' && (
        <div className="font-mono text-gray-800 dark:text-gray-200">picked: {r.choice_id?.toUpperCase() || '—'}</div>
      )}
      {stage === 'stage_3' && r.tile_results && (
        <div className="space-y-0.5 text-xs">
          {r.tile_results.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              {t.is_correct ? (
                <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <X className="w-3.5 h-3.5 text-rose-500 dark:text-rose-400" />
              )}
              <span className="font-serif">{t.lemma}</span>
              <span className="text-gray-400">→</span>
              <span className="text-gray-600 dark:text-gray-400">
                {t.assigned_word_id ? `column word_id=${t.assigned_word_id}` : 'discarded'}
              </span>
            </div>
          ))}
        </div>
      )}
      {stage === 'stage_4' && (
        <div className="font-mono text-gray-800 dark:text-gray-200">"{r.response}"</div>
      )}
      {item.kind === 'review_item' && (
        <div className="font-mono text-gray-800 dark:text-gray-200">
          {typeof r.text === 'string' ? `"${r.text}"` : null}
          {typeof r.response === 'string' ? `"${r.response}"` : null}
          {r.choice_id && <>picked: {r.choice_id.toUpperCase()}</>}
          {r.attempts != null && (
            <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">({r.attempts} attempts)</span>
          )}
        </div>
      )}
    </div>
  );
}

function ItemVerdict({ item }) {
  const v = item.verdict;
  if (!v) return null;
  const stage = item.stage_or_format;

  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Verdict</div>
      <div className="text-sm text-gray-700 dark:text-gray-300 space-y-0.5">
        {stage === 'stage_1' && (
          <div>
            Final tier: <TierPill tier={v.final_tier} /> {v.passed === true ? '· passed' : v.passed === false ? '· not passed' : ''}
          </div>
        )}
        {stage === 'stage_2_5' && (
          <div>
            {v.correct ? 'Correct' : 'Wrong'} · correct choice: <b>{v.correct_choice_id?.toUpperCase() || '—'}</b>
          </div>
        )}
        {stage === 'stage_3' && (
          <div>
            {v.pass ? 'Passed' : 'Not passed'} · {v.correct} of {v.total} correct ({Math.round((v.score || 0) * 100)}%)
          </div>
        )}
        {stage === 'stage_4' && (
          <div>
            rating: <b>{v.rating}</b>
            {v.kind && <> · kind: <span className="font-mono">{v.kind}</span></>}
            <> · accepted: <span className="font-mono">{v.correct_answer}</span></>
          </div>
        )}
        {item.kind === 'review_item' && (
          <div>
            {v.correct ? 'Correct' : 'Wrong'} · FSRS rating <b>{v.rating}</b>
            {v.correct_answer && <> · accepted: <span className="font-mono">{v.correct_answer}</span></>}
          </div>
        )}
      </div>
    </div>
  );
}

function OutcomeBadge({ outcome }) {
  if (outcome === 'good') {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-emerald-400 dark:border-emerald-600 flex-shrink-0">
        <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
      </span>
    );
  }
  if (outcome === 'hard') {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-gray-400 dark:border-gray-500 flex-shrink-0">
        <Check className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" strokeWidth={2.5} />
      </span>
    );
  }
  if (outcome === 'again' || outcome === 'wrong') {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-rose-400 dark:border-rose-500 flex-shrink-0">
        <X className="w-3.5 h-3.5 text-rose-500 dark:text-rose-400" strokeWidth={2.5} />
      </span>
    );
  }
  if (outcome === 'pass') {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-emerald-400 dark:border-emerald-600 flex-shrink-0">
        <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
      </span>
    );
  }
  return <span className="w-6 h-6 flex-shrink-0" />;
}

function TierPill({ tier }) {
  const styles = {
    yes: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    sort_of: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    way_off: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  };
  return (
    <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide', styles[tier] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300')}>
      {tier?.replace('_', ' ') || '—'}
    </span>
  );
}

function stageOrFormatLabel(s) {
  return {
    stage_1: 'Context',
    stage_2_5: 'Recognition',
    stage_3: 'Sort',
    stage_4: 'Recall',
    cloze: 'Cloze',
    mcq_def_to_word: 'MCQ',
    sentence_writing: 'Sentence',
  }[s] || s;
}

function outcomeFor(item) {
  const v = item.verdict;
  if (!v) return null;
  const s = item.stage_or_format;
  if (s === 'stage_1') return v.passed ? 'pass' : (v.final_tier === 'sort_of' ? 'hard' : 'again');
  if (s === 'stage_2_5') return v.correct ? 'good' : 'wrong';
  if (s === 'stage_3') return v.pass ? 'pass' : 'wrong';
  if (s === 'stage_4') return v.rating === 'good' ? 'good' : v.rating === 'hard' ? 'hard' : 'again';
  // review items use numeric rating: 1=again, 2=hard, 3=good, 4=easy
  if (item.kind === 'review_item') {
    if (v.rating == null) return v.correct ? 'good' : 'wrong';
    if (v.rating >= 3) return 'good';
    if (v.rating === 2) return 'hard';
    return 'again';
  }
  return null;
}

function formatDurationMinutes(seconds) {
  if (!seconds || seconds < 0) return null;
  const min = Math.floor(seconds / 60);
  const sec = Math.round(seconds % 60);
  if (min === 0) return `${sec}s`;
  return `${min}m ${sec}s`;
}
